import type { ProcessRunDetailDto, ProcessRunNodeStateDto, RunStatus } from '@api/processRuns';
import { latestStateByNode } from '../executionOrder';
import type {
  GateWaiting,
  ManualWaiting,
  NodeLiveState,
  ProcessRunSessionState,
  RunnerWaiting,
} from './types';

export const initialState = (): ProcessRunSessionState => ({
  active: null,
  perNode: new Map(),
  gateWaiting: null,
  runnerWaiting: null,
  manualWaiting: null,
  lastTerminalRun: null,
  startInFlight: false,
  inputError: null,
  streamDisconnected: false,
  isReconnecting: false,
});

export interface SeededState {
  readonly perNode: Map<string, NodeLiveState>;
  readonly gateWaiting: GateWaiting | null;
  readonly runnerWaiting: RunnerWaiting | null;
  readonly manualWaiting: ManualWaiting | null;
  readonly runStatus: RunStatus;
}

/** Rebuild {@link NodeLiveState.startedSeq} from the timestamps the live
 *  envelopes lack, so a reconnect doesn't reshuffle the execution log. */
const startSeqByNode = (
  latest: Map<string, ProcessRunNodeStateDto>,
): Map<string, number> => {
  const started = [...latest.values()]
    .filter((ns) => ns.startedAt !== null)
    .sort((a, b) => Date.parse(a.startedAt!) - Date.parse(b.startedAt!));
  return new Map(started.map((ns, i) => [ns.nodeId, i]));
};

const toLivePerNode = (
  latest: Map<string, ProcessRunNodeStateDto>,
): Map<string, NodeLiveState> => {
  const startSeq = startSeqByNode(latest);
  const perNode = new Map<string, NodeLiveState>();
  for (const [nodeId, ns] of latest.entries()) {
    perNode.set(nodeId, {
      status: ns.status,
      iteration: ns.iteration,
      startedSeq: startSeq.get(nodeId),
      errorMessage: ns.errorMessage,
      errorDetail: ns.errorDetail ?? null,
      executionTimeMs: ns.executionTimeMs ?? null,
      creditsSpent: ns.creditsSpent ?? null,
      artifacts: ns.artifacts,
      transcript: [],
    });
  }
  return perNode;
};

interface ResolvedWaiting {
  readonly gateWaiting: GateWaiting | null;
  readonly runnerWaiting: RunnerWaiting | null;
  readonly manualWaiting: ManualWaiting | null;
}

const resolveWaiting = (
  detail: ProcessRunDetailDto,
  latest: Map<string, ProcessRunNodeStateDto>,
): ResolvedWaiting => {
  const decided = new Set<string>();
  for (const d of detail.gateDecisions) {
    decided.add(`${d.nodeId}:${d.iteration}`);
  }
  let gateWaiting: GateWaiting | null = null;
  let runnerWaiting: RunnerWaiting | null = null;
  let manualWaiting: ManualWaiting | null = null;
  for (const ns of latest.values()) {
    const waiting = { runId: detail.run.runId, nodeId: ns.nodeId, iteration: ns.iteration };
    // A local task node parked for a runner and a manual node awaiting an
    // assignee each keep their own node-level discriminator; a manual gate
    // wait keeps the node RUNNING.
    if (ns.status === 'waiting_for_runner') {
      runnerWaiting ??= waiting;
      continue;
    }
    if (ns.status === 'waiting_for_user') {
      manualWaiting ??= waiting;
      continue;
    }
    if (ns.status !== 'running') continue;
    if (decided.has(`${ns.nodeId}:${ns.iteration}`)) continue;
    gateWaiting ??= waiting;
  }
  return { gateWaiting, runnerWaiting, manualWaiting };
};

/**
 * Build a state slice from the durable run detail payload. Used on
 * reconnect (page refresh while a run is in flight, or SSE drop mid-run)
 * to repopulate `perNode` + `gateWaiting` from persisted backend state.
 */
export const seedFromRunDetail = (detail: ProcessRunDetailDto): SeededState => {
  const latest = latestStateByNode(detail.nodeStates);
  const perNode = toLivePerNode(latest);
  const { gateWaiting, runnerWaiting, manualWaiting } =
    detail.run.status === 'waiting_for_action'
      ? resolveWaiting(detail, latest)
      : { gateWaiting: null, runnerWaiting: null, manualWaiting: null };
  return { perNode, gateWaiting, runnerWaiting, manualWaiting, runStatus: detail.run.status };
};

/**
 * Carry live transcripts across a reseed. {@link seedFromRunDetail}
 * rebuilds {@code perNode} from the run-detail DTO, which has no
 * transcript data — without this, every reconnect / reseed wiped the
 * streamed Agent Log. Only same-iteration transcripts survive: a new
 * iteration means the node restarted and the old log belongs to it.
 */
export const preserveTranscripts = (
  prevPerNode: ReadonlyMap<string, NodeLiveState>,
  seededPerNode: Map<string, NodeLiveState>,
): Map<string, NodeLiveState> => {
  for (const [nodeId, prevState] of prevPerNode) {
    if (prevState.transcript.length === 0) continue;
    const seeded = seededPerNode.get(nodeId);
    if (!seeded) continue;
    if (seeded.iteration === prevState.iteration && seeded.transcript.length === 0) {
      seededPerNode.set(nodeId, { ...seeded, transcript: prevState.transcript });
    }
  }
  return seededPerNode;
};

/**
 * Map a terminal run status to its {@link LastTerminalRun} outcome.
 * Extracts the nested ternary for readability and reuse.
 */
export const terminalOutcome = (
  status: string,
): 'completed' | 'failed' | 'cancelled' => {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return 'failed';
};
